import { Button } from "@ttc3k/trekker";
import { NoteManyQuery } from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, styled } from "styled-system/jsx";

type EditNoteContentProps = {
  note?: NoteManyQuery["noteMany"][0];
  viewOnly?: boolean;
  onEditSave: (message: string) => void;
  onDeleteNote?: () => void;
  onCloseEditing: () => void;
};

/**
 * Allows users to create, edit note messages here as well as to delete a note
 */
export const EditNoteContent = ({
  note,
  viewOnly = false,
  onEditSave,
  onDeleteNote,
  onCloseEditing,
}: EditNoteContentProps) => {
  const [value, setValue] = useState(note?.message ?? "");
  const { t } = useTranslation();

  const isCreatingNote = !note;
  const isDirty = value !== note?.message;

  const handleCancelClick = () => {
    setValue(note?.message ?? "");
    onCloseEditing();
  };

  const handleSaveClick = () => {
    onEditSave(value);
    // Only close the note here when you're editing one, not creating
    // Leave the closure of the note up to the caller when creating a note
    if (!isCreatingNote) onCloseEditing();
  };

  const handleDeleteClick = () => {
    onDeleteNote?.();
    onCloseEditing();
  };
  return (
    <>
      <styled.textarea
        width={"full"}
        minH={"140px"}
        border={"none"}
        color={"text.dark"}
        _placeholder={{ color: "text.highlight.dark" }}
        fontFamily={"body"}
        fontSize={"md"}
        lineHeight={"sm"}
        fontWeight={"normal"}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        placeholder={`${t("core:NOTES.PLACEHOLDER")}...`}
        _focusVisible={{
          outline: "none",
        }}
      />

      {!viewOnly && (
        <Flex
          gap={"250"}
          justifyContent={
            isCreatingNote || !onDeleteNote ? "flex-end" : "space-between"
          }
          alignItems={"flex-end"}
          // Added padding to ensure no layout change when switching between
          // editing mode & static content mode
          pt={"150"}
        >
          {onDeleteNote && (
            <Button
              size={"sm"}
              visual={"destructive"}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          )}
          <Flex gap={"100"}>
            <Button
              size={"sm"}
              visual={"primary"}
              mode={"light"}
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
            <Button
              size={"sm"}
              onClick={handleSaveClick}
              disabled={value.length < 1 || !isDirty}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
};
